import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, UseFormSetValue } from 'react-hook-form';
import {
  AppCheckbox,
  AppCheckboxFormLabel,
  AppDivider,
  AppForm,
  AppFormLabel,
  AppTextField,
  AppTypography,
} from '@/components/atoms';
import { R2_SYMPTOM_JOINT_OTHER_ID } from '@/constants/consentForm';
import type { ConsentFormData } from '@/schemas/consentSchema';
import type { ConsentOption, LabelChecks, SetLabelChecks } from '../types';
import { ensureArray } from '../utils';

interface ServiceSpecificTreatmentFieldsProps {
  control: Control<ConsentFormData>;
  hotTreatmentTypeOptions: ConsentOption[];
  isA2: boolean;
  isNotTransportCost: unknown;
  isSymptomJointOtherSelected: boolean;
  labelChecks: LabelChecks;
  massagePartOptions: ConsentOption[];
  massagePartsSel: string[];
  orthomorphiaPartOptions: ConsentOption[];
  orthomorphiaPartsSel: string[];
  setLabelChecks: SetLabelChecks;
  setValue: UseFormSetValue<ConsentFormData>;
  symptomJointOptions: ConsentOption[];
  symptomMuscleOptions: ConsentOption[];
  treatmentContentOptions: ConsentOption[];
  walkingConditionId: unknown;
}

export const ServiceSpecificTreatmentFields: React.FC<ServiceSpecificTreatmentFieldsProps> = ({
  control,
  hotTreatmentTypeOptions,
  isA2,
  isNotTransportCost,
  isSymptomJointOtherSelected,
  labelChecks,
  massagePartOptions,
  massagePartsSel,
  orthomorphiaPartOptions,
  orthomorphiaPartsSel,
  setLabelChecks,
  setValue,
  symptomJointOptions,
  symptomMuscleOptions,
  treatmentContentOptions,
  walkingConditionId,
}) => {
  if (isA2) {
    return (
      <>
        <div className="flex items-center gap-2">
          <AppFormLabel label="施術内容" required className="ml-[24.4px] flex-1" />
        </div>
        <div>
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <Controller
                name="treatment_content"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0 [&_.ant-form-item-control-input]:items-start [&_.ant-form-item-control-input]:min-h-0"
                  >
                    <AppCheckbox.Group
                      {...field}
                      value={ensureArray(field.value)}
                      onChange={v => field.onChange(ensureArray(v))}
                      className="grid w-full grid-cols-5 gap-1"
                      options={treatmentContentOptions}
                    />
                  </AppForm.Item>
                )}
              />
            </div>
            <div className="w-[200px] shrink-0 flex justify-end">
              <AhakiBothTreatmentCheckbox
                control={control}
                disabled={walkingConditionId !== 1 || !!isNotTransportCost}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <AppCheckboxFormLabel
          label="症状"
          required
          checked={!!labelChecks.symptom}
          onChange={e => setLabelChecks(prev => ({ ...prev, symptom: e.target.checked }))}
          className="flex-1"
        />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0 space-y-4">
          <div>
            <AppTypography.Text className="block mb-2 text-sm text-gray-600">
              筋麻痺・筋萎縮
            </AppTypography.Text>
            <Controller
              name="symptom_muscle"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0"
                >
                  <AppCheckbox.Group
                    {...field}
                    value={ensureArray(field.value)}
                    onChange={v => field.onChange(ensureArray(v))}
                    className="grid w-full grid-cols-5 gap-1"
                    options={symptomMuscleOptions}
                  />
                </AppForm.Item>
              )}
            />
          </div>
          <AppDivider className="my-0" />
          <div>
            <AppTypography.Text className="block mb-2 text-sm text-gray-600">
              関節拘縮
            </AppTypography.Text>
            <Controller
              name="symptom_joint"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0"
                >
                  <AppCheckbox.Group
                    {...field}
                    value={ensureArray(field.value)}
                    className="grid w-full grid-cols-6 gap-1"
                    onChange={nextValues => {
                      const prev = ensureArray(field.value);
                      const nextArr = ensureArray(nextValues);
                      field.onChange(nextArr);
                      if (
                        prev.includes(R2_SYMPTOM_JOINT_OTHER_ID) &&
                        !nextArr.includes(R2_SYMPTOM_JOINT_OTHER_ID)
                      ) {
                        setValue('symptom_joint_other', '', {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                    options={symptomJointOptions}
                  />
                </AppForm.Item>
              )}
            />
            <div className="mt-2">
              <Controller
                name="symptom_joint_other"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppForm.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                    className="mb-0"
                  >
                    <AppTextField
                      {...field}
                      value={field.value || ''}
                      maxLength={255}
                      disabled={!isSymptomJointOtherSelected}
                      status={error ? 'error' : ''}
                    />
                  </AppForm.Item>
                )}
              />
            </div>
          </div>
          <AppDivider className="my-0" />
          <div>
            <AppTypography.Text className="block mb-2 text-sm text-gray-600">
              その他
            </AppTypography.Text>
            <Controller
              name="symptom_other"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0"
                >
                  <AppTextField
                    {...field}
                    value={field.value || ''}
                    maxLength={255}
                    status={error ? 'error' : ''}
                  />
                </AppForm.Item>
              )}
            />
          </div>
        </div>
        <div className="pt-1 w-[200px] shrink-0 flex justify-end">
          <Controller
            name="is_symptom_print"
            control={control}
            render={({ field }) => (
              <AppCheckbox
                {...field}
                checked={field.value}
                onChange={e => field.onChange(e.target.checked)}
                className="whitespace-nowrap"
              >
                部位をレセプトに印字する
              </AppCheckbox>
            )}
          />
        </div>
      </div>
      <div className="col-span-2">
        <AppDivider className="my-0" />
      </div>
      <div className="flex items-center gap-2">
        <AppCheckboxFormLabel
          label="施術の種類・施術部位"
          required
          checked={!!labelChecks.treatment_parts}
          onChange={e => setLabelChecks(prev => ({ ...prev, treatment_parts: e.target.checked }))}
          className="flex-1"
        />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0 space-y-4">
          <div>
            <AppTypography.Text className="block mb-2 text-sm font-medium">
              マッサージ
            </AppTypography.Text>
            <Controller
              name="massage_parts"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0"
                >
                  <AppCheckbox.Group
                    {...field}
                    value={ensureArray(field.value)}
                    className="grid w-full grid-cols-5 gap-1"
                    options={massagePartOptions}
                    onChange={next => {
                      const nextArr = ensureArray(next);
                      field.onChange(nextArr);
                      const op = orthomorphiaPartsSel || [];
                      const stripped = op.filter(x => !nextArr.includes(x));
                      if (stripped.length !== op.length) {
                        setValue('orthomorphia_parts', stripped, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                      if (nextArr.length === 0 && orthomorphiaPartsSel.length === 0) {
                        setValue('hot_treatment_type_id', null, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                  />
                </AppForm.Item>
              )}
            />
          </div>
          <AppDivider className="my-0" />
          <div>
            <AppTypography.Text className="block mb-2 text-sm font-medium">
              マッサージ＋変形徒手矯正術
            </AppTypography.Text>
            <Controller
              name="orthomorphia_parts"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0"
                >
                  <AppCheckbox.Group
                    {...field}
                    value={ensureArray(field.value)}
                    className="grid w-full grid-cols-5 gap-1"
                    options={orthomorphiaPartOptions}
                    onChange={next => {
                      const nextArr = ensureArray(next);
                      field.onChange(nextArr);
                      const mp = massagePartsSel || [];
                      const stripped = mp.filter(x => !nextArr.includes(x));
                      if (stripped.length !== mp.length) {
                        setValue('massage_parts', stripped, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        if (stripped.length === 0) {
                          setValue('hot_treatment_type_id', null, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }
                    }}
                  />
                </AppForm.Item>
              )}
            />
          </div>
          <AppDivider className="my-0" />
          <div>
            <AppTypography.Text className="block mb-2 text-sm text-gray-600">
              罨法と電療
            </AppTypography.Text>
            <Controller
              name="hot_treatment_type_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0"
                >
                  <div className="flex flex-row gap-6">
                    {hotTreatmentTypeOptions.map(option => (
                      <AppCheckbox
                        key={option.value}
                        checked={field.value === option.value}
                        disabled={massagePartsSel.length === 0}
                        onChange={e => {
                          field.onChange(e.target.checked ? option.value : null);
                        }}
                      >
                        {option.label}
                      </AppCheckbox>
                    ))}
                  </div>
                </AppForm.Item>
              )}
            />
          </div>
        </div>
        <div className="pt-1 w-[200px] shrink-0 flex justify-end">
          <AhakiBothTreatmentCheckbox
            control={control}
            disabled={walkingConditionId !== 1 || !!isNotTransportCost}
            className="whitespace-nowrap"
          />
        </div>
      </div>
    </>
  );
};

interface AhakiBothTreatmentCheckboxProps {
  className?: string;
  control: Control<ConsentFormData>;
  disabled: boolean;
}

const AhakiBothTreatmentCheckbox: React.FC<AhakiBothTreatmentCheckboxProps> = ({
  className,
  control,
  disabled,
}) => (
  <Controller
    name="is_ahaki_both_treatment"
    control={control}
    render={({ field }) => (
      <AppCheckbox
        {...field}
        checked={field.value}
        disabled={disabled}
        onChange={e => {
          if (e.target.checked) {
            const ok = window.confirm(
              '同一日に同一の患者に対してはり、きゅう及びマッサージ両方の訪問施術を行い、この同意書に紐づく施術を訪問施術料で請求する場合にチェックします。チェックして宜しいですか？'
            );
            if (ok) field.onChange(true);
          } else {
            field.onChange(false);
          }
        }}
        className={className}
      >
        あはき両方施術
      </AppCheckbox>
    )}
  />
);
